import React, { useState } from 'react';
import { TextField, IconButton, InputAdornment, TextFieldProps } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useController, Control } from 'react-hook-form';

type CustomTextFieldProps = TextFieldProps & {
  label?: string;
  type?: string;
  name?: string;
  control?: Control<any>;
};

const BaseTextField: React.FC<CustomTextFieldProps & { field?: any; fieldState?: any }> = (props) => {
  const { label, type = 'text', field, fieldState, onBlur: restOnBlur, onChange: restOnChange, ...rest } = props;
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePassword = () => setShowPassword((prev) => !prev);
  const isPassword = type === 'password';

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const trimmed = e.target.value.trim();

    if (field) {
      // react-hook-form controlled field
      field.onChange(trimmed);
      field.onBlur();
    } else if (restOnChange) {
      //Create a synthetic change event with trimmed value
      const syntheticEvent = {
        target: { value: trimmed },
        currentTarget: { value: trimmed }
      } as React.ChangeEvent<HTMLInputElement>;
      restOnChange(syntheticEvent);
    }

    if (restOnBlur) {
      restOnBlur(e);
    }
  };

  return (
    <TextField
      label={label}
      type={isPassword ? (showPassword ? 'text' : 'password') : type}
      variant="outlined"
      fullWidth
      InputLabelProps={{ shrink: true }}
      autoComplete="off"
      InputProps={
        isPassword
          ? {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end" onClick={handleTogglePassword}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }
          : undefined
      }
      {...field}
      onBlur={handleBlur}
      error={fieldState ? !!fieldState.error?.message : false}
      helperText={fieldState ? fieldState.error?.message : ''}
      onChange={field ? field.onChange : restOnChange}
      {...rest}
    />
  );
};

const ControlledTextField: React.FC<CustomTextFieldProps> = (props) => {
  const { name, control } = props;
  const { field, fieldState } = useController({ name: name!, control: control! });
  return <BaseTextField {...props} field={field} fieldState={fieldState} />;
};

const CustomTextField: React.FC<CustomTextFieldProps> = (props) => {
  const { name, control } = props;
  return control && name ? <ControlledTextField {...props} /> : <BaseTextField {...props} />;
};

export default CustomTextField;
